<template>
  <div v-if="(user && config) || (isDemo && config)" class="relative">
    <TopBar v-if="!isOnboardingShell" />
    <Sidebar v-if="!isOnboardingShell" />
    <OnboardingShellHeader v-if="isOnboardingShell" />
    <div
      class="overflow-hidden"
      :class="[
        isOnboardingShell
          ? 'onboarding-shell-canvas min-h-dvh pt-16'
          : 'min-h-screen bg-slate-50 lg:pt-12 dark:bg-slate-900',
        !route.meta?.fullScreen && !isOnboardingShell ? 'lg:pl-64' : '',
        isDemo ? 'pb-14' : ''
      ]">
      <div
        :class="
          isOnboardingShell
            ? 'mx-auto min-h-[calc(100dvh-4rem)] max-w-5xl px-4 pb-10 pt-12 sm:px-6 sm:pt-14 lg:px-8'
            : route.meta?.fullScreen
              ? ''
              : 'mx-auto max-w-7xl space-y-12 px-4 py-10 sm:px-6 lg:px-8'
        ">
        <router-view />
      </div>
    </div>

    <ConnectionLostBanner />

    <!-- Lazy-loaded Command Palette (Cmd+K) -->
    <CommandPalette v-if="!isOnboardingShell && commandPaletteOpen" v-model:open="commandPaletteOpen" />

    <!-- Outcome chooser lives here (not inside callers) so it survives
         surfaces that unmount themselves, like the command palette -->
    <OutcomeChooserDialog
      v-if="!isOnboardingShell"
      :visible="outcomeChooserVisible"
      :source="outcomeChooserSource"
      :selected-wizard-id="outcomeChooserWizardId"
      @update:visible="outcomeChooserVisible = $event" />

    <ProjectBillingSetupModal
      :visible="showProjectBillingSetupModal"
      :blocking="showProjectBillingSetupModal"
      :team-pid="route.params.teamPid as string"
      @update:visible="handleProjectBillingSetupVisibleChange" />

    <!-- Demo Mode -->
    <template v-if="isDemo">
      <DemoWelcomeOverlay />
      <DemoCtaBar />
    </template>

    <!-- Lazy-loaded Righty AI Assistant -->
    <template v-if="!isOnboardingShell && !isDemo && !showTrialExpiredModal">
      <RightyContextPrompts />
      <RightyWindow />
    </template>

    <!-- Account Overdue Modal -->
    <Upgrade v-if="showTrialExpiredModal" :closeable="false" :is-blocking="true" />
  </div>
  <div v-else-if="isError" class="flex h-screen items-center justify-center">
    <div class="text-center">
      <h1 class="text-2xl font-bold">There's an error loading your team's configuration.</h1>
      <p class="text-gray-500">Please contact support at support@rightmessage.com</p>
      <div class="mt-4">
        <TeamDropdown class="w-3xl" />
      </div>
    </div>
  </div>

  <!-- Loading state - matches inline loader for seamless transition -->
  <div v-else class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900">
    <svg
      class="h-12 w-12 animate-spin text-slate-700 dark:text-slate-200"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
    <p class="mt-4 text-sm text-slate-500">Loading RightMessage...</p>
  </div>
</template>

<script setup lang="ts">
import { useOutcomeChooser } from '@/modules/activation/composables/useOutcomeChooser'
import { useBillingPortalErrorNotification } from '@/composables/useBillingPortalErrorNotification'
import { useRightyKeyboardShortcuts } from '@/modules/righty'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { useSubscriptionStore } from '@/stores/subscriptionStore'
import DemoCtaBar from '@/components/common/DemoCtaBar.vue'
import DemoWelcomeOverlay from '@/components/common/DemoWelcomeOverlay.vue'

// Lazy-load heavy components to reduce initial bundle size
const RightyWindow = defineAsyncComponent(() => import('@/modules/righty/components/RightyWindow.vue'))
const RightyContextPrompts = defineAsyncComponent(() => import('@/modules/righty/components/RightyContextPrompts.vue'))
const CommandPalette = defineAsyncComponent(() => import('@/components/common/CommandPalette.vue'))
const OutcomeChooserDialog = defineAsyncComponent(
  () => import('@/modules/activation/components/OutcomeChooserDialog.vue')
)

const {
  visible: outcomeChooserVisible,
  source: outcomeChooserSource,
  selectedWizardId: outcomeChooserWizardId
} = useOutcomeChooser()
const Upgrade = defineAsyncComponent(() => import('@/modules/settings/pages/Upgrade.vue'))
const ProjectBillingSetupModal = defineAsyncComponent(() => import('@/components/common/ProjectBillingSetupModal.vue'))

const route = useRoute()

// Initialize Righty keyboard shortcuts (Cmd+J to open, Escape to close)
useRightyKeyboardShortcuts()

useBillingPortalErrorNotification()

// Bridge user/plan state to window.rm_app_user for RightMessage tag.js flows
useRightMessageBridge()

const authStore = useAuthStore()
const { user, isAdmin } = storeToRefs(authStore)
const configStore = useConfigStore()
const { config, currentTeamPid, isError } = storeToRefs(configStore)
const { canAccess } = usePermissionStore()
provide('canAccess', canAccess)

useTeamStore()

const { identifyUser, setTeamGroup } = usePostHog()

const { isOpen: commandPaletteOpen } = useCommandPalette()

// Check if we're in demo mode
const isDemo = computed(() => route.params.teamPid === 'demo')
const onboardingStore = useOnboardingStore()
const { hasMeaningfulFirstWinProgress } = storeToRefs(onboardingStore)
const isOnboardingShell = computed(() => route.meta?.shell === 'onboarding' && !hasMeaningfulFirstWinProgress.value)

// Provide isDemo context for components that need to know if we're in demo mode
provide('isDemo', isDemo)

// Subscription state for overdue account modals
// Subscription state for overdue account modals and per-project billing setup.
const subscriptionStore = useSubscriptionStore()
const { isTrialExpired, needsCurrentTeamBillingSetup } = storeToRefs(subscriptionStore)

// Show trial expired modal for users whose trial ended without payment
const showTrialExpiredModal = computed(() => {
  if (isDemo.value) return false
  if (isAdmin.value) return false
  return isTrialExpired.value
})

const showProjectBillingSetupModal = computed(() => {
  if (isDemo.value || showTrialExpiredModal.value) return false
  return route.query.billingSetup === '1' && needsCurrentTeamBillingSetup.value
})

const router = useRouter()

const handleProjectBillingSetupVisibleChange = async (visible: boolean) => {
  if (visible || route.query.billingSetup !== '1') return

  const { billingSetup, ...query } = route.query
  await router.replace({ query })
}

const { currentTeam } = storeToRefs(configStore)

// Identify user to PostHog when authenticated
watch(
  user,
  (newUser) => {
    if (newUser && !isDemo.value) {
      identifyUser({
        id: newUser.id,
        email: newUser.email,
        name: newUser.name,
        created_at: newUser.created_at,
        trial_ends_at: newUser.trial_ends_at,
        free_account: newUser.free_account,
        on_trial: newUser.on_trial,
        current_billing_plan: newUser.current_billing_plan,
        stripe_status: newUser.stripe_status,
        active_subscription: newUser.active_subscription,
        is_agency: newUser.is_agency,
        first_promoter_ref_id: newUser.first_promoter_ref_id,
        owned_teams: newUser.owned_teams
      })
    }
  },
  { immediate: true }
)

// Set team group context when team changes
watch(
  currentTeam,
  (team) => {
    if (team && !isDemo.value) {
      setTeamGroup({
        pid: team.pid,
        name: team.name,
        current_billing_plan: team.current_billing_plan
      })
    }
  },
  { immediate: true }
)

watch(
  () => route.params.teamPid,
  () => {
    const teamPid = route.params.teamPid as string
    // Handle demo mode - keep as string 'demo', otherwise parse as integer
    currentTeamPid.value = teamPid === 'demo' ? 'demo' : parseInt(teamPid)
  },
  { immediate: true }
)
</script>

<style scoped>
.onboarding-shell-canvas {
  --dot-bg: #f8fafc;
  --dot-color: #d4dbe4;
  --dot-size: 1px;
  --dot-space: 16px;
  background-color: var(--dot-bg);
  background-image: radial-gradient(circle, var(--dot-color) var(--dot-size), transparent var(--dot-size));
  background-size: var(--dot-space) var(--dot-space);
  background-position: 0 0;
}

:deep(.dark) .onboarding-shell-canvas,
.dark .onboarding-shell-canvas {
  --dot-bg: #1e1e2e;
  --dot-color: rgba(110, 106, 134, 0.3);
}
</style>
